import React, { Component } from "react";
import { Redirect } from "react-router-dom";
import InputText from "../../components/InputText.jsx";
import TranslationText from "../../components/Translation/TranslationText.jsx";
import { Trans, withTranslation } from 'react-i18next';
import RimiLogoHeaderButton from "../../components/HeaderComponents/RimiLogoHeaderButton.jsx";
import AutoHideableHeader from "../../components/HeaderComponents/AutoHideableHeader.jsx";
import StepBackHeader from "../../components/HeaderComponents/StepBackHeader.jsx";
import validator from "../../components/FieldValidator.jsx";
import EmailAndPhoneValidator from "../../components/EmailAndPhoneValidator.jsx";
import ConsentLinks from "../../components/Consents/ConsentLinks.jsx";
import Footer from "../../components/Footer.jsx";
import SendVerificationCode from "../../components/Buttons/SendVerificationCode.jsx";
import TryAgainFooterButtons from "../../components/Buttons/TryAgainFooterButtons.jsx";
import { GoogleReCaptcha } from "react-google-recaptcha-v3";
import fetcherApi from '../../components/FetcherApi.jsx';
import urlTools from "../../tools/urlTools.jsx";
import LegalEntityFunctions from "../../components/LegalEntityFunctions.jsx";
import * as constants from "../../constants/constants.jsx";

class SmartIdRegisterWithEmail extends Component {

  state = {
    email: "",
    error: false,
    errorText: "",
    isProcessing: false,
    verificationCodeSent: false,
    beginRecaptchaValidation: undefined
  };

  componentDidMount() {
    // Ensure we have the required Smart-ID registration data
    const locationState = this.props.location?.state;
    
    if (!locationState?.isSmartIdRegistration) {
      // Redirect back to create account if missing required data
      this.props.history.push('/registration/NoAccount/CreateNewMyRimiAccount');
    }
  }

  onChange = e => {
    e.value = validator.blockSpaceSymbol(e.value);
    this.setState({
      email: e.value,
      error: false,
      errorText: '',
      beginRecaptchaValidation: undefined
    });
  };

  validateEmail = () => {
    const { email } = this.state;
    
    if (!email || email.trim().length === 0) {
      this.setState({ 
        error: true, 
        errorText: 'empty_email_error_message' 
      });
      return false;
    }

    const emailStructureError = EmailAndPhoneValidator.getEmailStructureError(email);
    if (emailStructureError) {
      this.setState({ 
        error: true, 
        errorText: emailStructureError 
      });
      return false;
    }

    return true;
  };

  beginRecaptchaValidation = () => {
    if (this.state.verificationCodeSent || this.state.isProcessing) {
      return;
    }

    if (!this.validateEmail()) {
      return;
    }

    this.setState((prevState) => ({
      beginRecaptchaValidation: !prevState.beginRecaptchaValidation
    }));
  };

  completeRecaptchaValidation = (recaptchaToken) => {
    this.setState({ isProcessing: false }, () => {
      this.continue(recaptchaToken);
    });
  };

  continue = async (recaptchaToken) => {
    if (this.state.verificationCodeSent || this.state.isProcessing) {
      return;
    }

    this.setState({ isProcessing: true });

    if (!this.validateEmail()) {
      this.setState({ isProcessing: false });
      return;
    }

    try {
      const selectedCountry = LegalEntityFunctions.getLegalEntityId(localStorage.getItem(constants.SELECTED_COUNTRY));
      
      const requestSettings = { recaptchaToken };
      const url = urlTools.constructRoute('api/RegistrationCode/send-code-smart-id/' + selectedCountry + '/' + this.state.email);
      const httpResponse = await fetcherApi.post(url, null, requestSettings);
      const result = httpResponse.response;

      if (result.data.success) {
        this.setState({ 
          verificationCodeSent: true,
          isProcessing: false 
        });
      } else {
        let errorText = result.data.error;
        if (result.data.error === "verification_resend_limit_reached") {
          errorText = "create_new_account_page_email_limit_error";
        } else if (result.data.error === "email_incorrect_format") {
          errorText = "account_found_but_no_email_page_in_login_empty_email_field_error";
        } else if (result.data.error === "email_placeholer") {
          errorText = "account_found_but_no_email_page_incorrect_email_error";
        }
        
        this.setState({
          error: true,
          errorText: errorText,
          isProcessing: false,
          beginRecaptchaValidation: undefined
        });
      }
    } catch (ex) {
      this.setState({
        error: true,
        errorText: "unexpected_error",
        isProcessing: false,
        beginRecaptchaValidation: undefined
      });
    }
  };

  renderButtons() {
    if (!this.state.error && !this.state.verificationCodeSent) {
      return <SendVerificationCode 
        confirm={this.beginRecaptchaValidation} 
        disabled={this.state.beginRecaptchaValidation !== undefined || this.state.isProcessing} 
      />
    }
    else if (!this.state.verificationCodeSent) {
      return <TryAgainFooterButtons 
        confirm={this.beginRecaptchaValidation} 
        isProcessing={this.state.isProcessing} 
        disabled={this.state.beginRecaptchaValidation !== undefined || this.state.isProcessing} 
      />
    }
  }

  renderBottom() {
    if (!this.state.error) {
      return (
        <footer className="card-footer no-border">
          <div className="footer-text">
            <ConsentLinks />
          </div>
        </footer>
      )
    }
    else if (!this.state.verificationCodeSent) {
      return (
        <footer className="card-footer">
          <div className="footer-text">
            <ConsentLinks />
          </div>
        </footer>
      )
    }
  }

  render() {
    if (this.state.verificationCodeSent) {
      return <Redirect to={{
        pathname: '/VerificationCodeRegistrationB2C',
        state: {
          codeSentTo: this.state.email,
          previousPath: this.props.location.pathname,
          nextStepState: {
            isSmartIdRegistration: true,
            personalInfo: this.props.location.state.personalInfo
          }
        }
      }} />;
    }

    return (
      <React.Fragment>
        <div className="container">
          <div className="card">
            <AutoHideableHeader>
              <RimiLogoHeaderButton initialData={this.props.initialData} />
              <StepBackHeader
                pathName='/registration/NoAccount/CreateNewMyRimiAccount'
                buttonText={'step_back_button_label'} />
            </AutoHideableHeader>
            <div className={this.state.error ? "card-content" : "card-content no-padding-bottom"}>
              <h1 className="register-smartid-title">
                <Trans i18nKey='smart_id_register_with_email_title' components={{ bold: <b /> }} />
              </h1>
              
              <p><TranslationText text='smart_id_register_with_email_description' /></p>

              <div className="form-group">
                <div className="form-row">
                  <div className={!this.state.error ? "form-field" : "form-field -has-error"}>
                    <label htmlFor="email" className="form-field__label">
                      <TranslationText text='email_field_label' />
                    </label>
                    <InputText
                      onChange={this.onChange}
                      prop="email"
                      value={this.state.email}
                      className="form-field__input"
                      placeholder="example@gmail.com"
                      disabled={this.state.isProcessing}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="sublabel error-msg show">
                    <span>{<TranslationText text={this.state.errorText} />}</span>
                  </div>
                </div>
              </div>

              {
                this.state.beginRecaptchaValidation !== undefined
                  ? <GoogleReCaptcha onVerify={this.completeRecaptchaValidation} refreshReCaptcha={this.state.beginRecaptchaValidation} />
                  : null
              }

              {this.renderButtons()}
            </div>
            {this.renderBottom()}
          </div>
        </div>
        <Footer countrySelectionEnabled={false} />
      </React.Fragment>
    );
  }
}

export default withTranslation()(SmartIdRegisterWithEmail); 